<template>
    <vue-scroll>
        <div class="page-header">
            <h1>Pricing</h1>
        </div>
        <el-tabs v-model="activeTab">
            <el-tab-pane name="calendars" label="Calendars">
                <calendars :form="form" />
            </el-tab-pane>
            <el-tab-pane name="basic-combo" label="Basic Combo">
                <basic-combo :form="form" />
            </el-tab-pane>
            <el-tab-pane name="flex-four" label="Flex Four">
                <flex-four :form="form" />
            </el-tab-pane>
            <el-tab-pane name="simply-six" label="Simply Six">
                <simply-six :form="form" />
            </el-tab-pane>
            <el-tab-pane name="readers-delight" label="Reader's Delight">
                <readers-delight :form="form" />
            </el-tab-pane>
            <el-tab-pane name="additional-products" label="Additional Products">
                <additional-products :form="form" />
            </el-tab-pane>
            <el-tab-pane name="discounts" label="Discounts">
                <discounts />
            </el-tab-pane>
        </el-tabs>
        <div class="mt-10" v-if="can(Permissions.EDIT_PRICING)">
            <el-button
                :loading="form.busy"
                @click="submit"
                icon="mdi mdi-content-save"
                type="primary"
                v-if="this.activeTab != 'discounts'"
            >
                Save Changes
            </el-button>
        </div>
    </vue-scroll>
</template>

<script>
import * as ColorTypes from "@/constants/color-types";
import * as NewsletterTypes from "@/constants/newsletter-types";
import * as OrderableTypes from "@/constants/orderable-types";
import auth from "@/mixins/auth";
import unsaved_changes_mixin from "@/mixins/unsaved-changes";
import * as Getters from "@/store/constants/getters";
import { Money, VMoney } from "v-money";
import { mapGetters, mapState } from "vuex";
import AdditionalProducts from "./pricing/AdditionalProducts";
import base_mixin from "./pricing/base";
import BasicCombo from "./pricing/BasicCombo";
import Calendars from "./pricing/Calendars";
import Discounts from "./pricing/Discounts";
import FlexFour from "./pricing/FlexFour";
import ReadersDelight from "./pricing/ReadersDelight";
import SimplySix from "./pricing/SimplySix";

export default {
    mixins: [base_mixin, unsaved_changes_mixin, auth],
    beforeRouteEnter(to, from, next) {
        Promise.all([
            axios.get(`/web-api/price`),
            axios.get(`/web-api/price/additional-products`)
        ]).then((responses) => {
            next((vm) => vm.setResponses(responses));
        });
    },
    directives: { money: VMoney },
    components: {
        SimplySix,
        FlexFour,
        Money,
        Calendars,
        BasicCombo,
        ReadersDelight,
        AdditionalProducts,
        Discounts
    },
    data() {
        return {
            ColorTypes,
            form: new Form({
                prices: [],
                additional_products: []
            }),
            prices: [],
            money: {
                decimal: ".",
                thousands: ",",
                prefix: "$ ",
                suffix: "",
                precision: 2,
                masked: true
            },
            activeTab: "calendars"
        };
    },
    created() {},
    computed: {
        ...mapState({
            pricing: (state) => state.config.settings.pricing,
            sizes: (state) => state.sizes.data,
            pricing_models: (state) => state.pricing_models.data
        }),
        ...mapGetters([
            Getters.FLAT_SIZES,
            Getters.POSTER_SIZES,
            Getters.WEEKLY_CALENDAR_AVAILABLE_SIZES,
            Getters.WEEKLY_CALENDAR_AVAILABLE_POSTER_SIZES
        ]),
        hasChanges() {
            if (!this.oldData) {
                return false;
            }
            return this.oldData !== this.getCleanFormData();
        }
    },
    mounted() {
        this.initForm();
        this.setData(this.responses);
    },
    methods: {
        setResponses(responses) {
            this.responses = responses;
        },
        getCleanFormData() {
            let keys = _.keys(this.basePriceAttributes);
            keys.push("price");
            let data = {
                prices: this.form.prices.map((item) => _.pick(item, keys)),
                additional_products: this.form.additional_products.map((item) =>
                    _.pick(item, ["key", "label", "value"])
                )
            };
            //console.log(data);
            return JSON.stringify(data);
        },
        initForm() {
            let base_attributes = {
                ...this.basePriceAttributes,
                price: 0
            };
            this.form.setVeeErrors(this.veeErrors);
            let quantities = this.pricing.quantities.calendars.flats;
            _.forEach(this[Getters.FLAT_SIZES], (size) => {
                _.forEach(ColorTypes, (color_type) => {
                    _.forEach(quantities, (quantity) => {
                        this.form.prices.push({
                            ...base_attributes,
                            size_id: size.id,
                            orderable_type: OrderableTypes.CALENDAR,
                            color_type,
                            quantity
                        });
                    });
                });
            });

            // weekly calendars - flat size
            _.forEach(
                this.$store.getters[
                    Getters.WEEKLY_CALENDAR_AVAILABLE_FLAT_SIZES
                ],
                (size) => {
                    _.forEach(quantities, (quantity) => {
                        this.form.prices.push({
                            ...base_attributes,
                            size_id: size.id,
                            orderable_type: OrderableTypes.CALENDAR,
                            quantity,
                            weekly_calendar: true,
                            color_type: ColorTypes.COLORED
                        });
                    });
                }
            );

            quantities = this.pricing.quantities.calendars.posters;
            _.forEach(this[Getters.POSTER_SIZES], (size) => {
                _.forEach(ColorTypes, (color_type) => {
                    _.forEach(quantities, (quantity) => {
                        this.form.prices.push({
                            ...base_attributes,
                            size_id: size.id,
                            orderable_type: OrderableTypes.CALENDAR,
                            color_type,
                            quantity
                        });
                    });
                });
            });

            // weekly calendars - posters
            _.forEach(
                this[Getters.WEEKLY_CALENDAR_AVAILABLE_POSTER_SIZES],
                (size) => {
                    _.forEach(quantities, (quantity) => {
                        this.form.prices.push({
                            ...base_attributes,
                            size_id: size.id,
                            orderable_type: OrderableTypes.CALENDAR,
                            color_type: ColorTypes.COLORED,
                            quantity,
                            weekly_calendar: true
                        });
                    });
                }
            );

            quantities = this.pricing.quantities.calendars.flats;
            _.forEach(ColorTypes, (color_type) => {
                // basic combo
                quantities.forEach((quantity) => {
                    this.form.prices.push({
                        ...base_attributes,
                        newsletter_type_id: 1,
                        color_type,
                        quantity
                    });
                });
                // flex four
                quantities.forEach((quantity) => {
                    this.form.prices.push({
                        ...base_attributes,
                        newsletter_type_id: 2,
                        color_type,
                        quantity
                    });
                });
            });

            // Add weekly calendar prices for basic combo
            quantities.forEach((quantity) => {
                this.form.prices.push({
                    ...base_attributes,
                    newsletter_type_id: 1,
                    color_type: null,
                    weekly_calendar: true,
                    quantity
                });
            });

            // simply six
            quantities.forEach((quantity) => {
                this.form.prices.push({
                    ...base_attributes,
                    newsletter_type_id: 3,
                    quantity
                });
                this.form.prices.push({
                    ...base_attributes,
                    newsletter_type_id: 3,
                    each_calendar: true,
                    quantity
                });
            });

            // reader's delight
            quantities =
                this.pricing.newsletter_types[NewsletterTypes.READERS_DELIGHT]
                    .quantities;
            quantities.forEach((quantity) => {
                let base = {
                    ...base_attributes,
                    newsletter_type_id: 4,
                    quantity
                };
                this.form.prices.push(base);
                this.form.prices.push({
                    ...base,
                    monthly_calendar: true
                });
                this.form.prices.push({
                    ...base,
                    weekly_calendar: true
                });
            });

            quantities = this.pricing.quantities.calendars.posters;
            let base = {
                ...base_attributes,
                newsletter_type_id: 4,
                orderable_type: OrderableTypes.CALENDAR
            };
            quantities.forEach((quantity) => {
                _.extend(base, {
                    quantity
                });
                this[Getters.POSTER_SIZES].forEach((size) => {
                    this.form.prices.push({
                        ...base,
                        size_id: size.id,
                        monthly_calendar: true
                    });
                });
                this[Getters.WEEKLY_CALENDAR_AVAILABLE_POSTER_SIZES].forEach(
                    (size) => {
                        this.form.prices.push({
                            ...base,
                            size_id: size.id,
                            weekly_calendar: true
                        });
                    }
                );
            });
        },
        submit() {
            this.$validator.validate().then((valid) => {
                if (!valid) {
                    return;
                }
                window.App.post(`/web-api/price`, this.form, (data) => {
                    _.forEach(data.prices, (price) => {
                        if (!price.price) {
                            return;
                        }
                        if (typeof price.price === "number") {
                            return;
                        }
                        price.price = price.price
                            .replace("$ ", "")
                            .replace(",", "");
                    });
                    data.additional_products.forEach((item) => {
                        item.value = item.value
                            .replace("$", "")
                            .replace(",", "");
                    });
                }).then(() => {
                    this.$notify.success({
                        message: "Changes have been saved."
                    });
                    this.initOldData();
                });
            });
        },
        setData(responses) {
            responses[0].data.data.forEach((item) => {
                let attributes = [
                    "newsletter_type_id",
                    "size_id",
                    "orderable_type",
                    "color_type",
                    "quantity",
                    "newsletter_pages",
                    "max_chars",
                    "monthly_calendar",
                    "weekly_calendar",
                    "each_calendar"
                ];
                let temp = JSON.stringify(_.pick(item, attributes));
                let price = this.form.prices.find((price) => {
                    return JSON.stringify(_.pick(price, attributes)) == temp;
                });
                if (price) {
                    price.price = item.price;
                }
            });
            responses[1].data.forEach((item) => {
                this.form.additional_products.push(
                    _.pick(item, ["key", "label", "value"])
                );
            });
            Vue.nextTick(() => {
                this.initOldData();
            });
        }
    }
};
</script>

<style lang="scss" scoped></style>
